import type { AvailableLanguage } from './i18n';

/**
 * Updates the HTML document language attribute based on the selected UI language
 * @param language The currently selected UI language
 */
export function updateDocumentLanguage(language: AvailableLanguage): void {
  if (language === 'ja-JP') {
    document.documentElement.lang = 'ja';
  } else if (language === 'en-GB') {
    document.documentElement.lang = 'en';
  }
}

/**
 * Detects user's preferred language from browser settings
 * @returns The detected language as an AvailableLanguage ('en-GB' or 'ja-JP')
 */
export function detectBrowserLanguage(): AvailableLanguage {
  // Check localStorage first to see if the user has already set a preference
  const savedLanguage = localStorage.getItem('uiLanguage') as AvailableLanguage;
  if (savedLanguage === 'en-GB' || savedLanguage === 'ja-JP') {
    return savedLanguage;
  }
    // Get browser language preferences
  const browserLanguages = navigator.languages || [navigator.language || 'en'];
  
  // Check if any Japanese locale is preferred
  const hasJapanesePreference = browserLanguages.some(lang => 
    lang.toLowerCase().startsWith('ja') || 
    lang.toLowerCase() === 'ja-jp'
  );
  
  // Return Japanese if preferred, otherwise default to English
  return hasJapanesePreference ? 'ja-JP' : 'en-GB';
}

/**
 * 利用可能な認識言語の一覧を受け取り、ブラウザの言語設定に最も近い言語コードを返します
 * ブラウザの言語に一致するものがなければ英語（en-US）を返します
 * @param availableLanguages 利用可能な言語の配列 {code: string, name: string}
 * @returns 選択すべき言語コード
 */
export function getSpeechRecognitionLanguage(
  availableLanguages: Array<{ code: string; name: string }>
): string {
  // Get browser language preferences
  const browserLanguages = navigator.languages || [navigator.language || 'en'];
  
  // First try to find an exact match
  for (const browserLang of browserLanguages) {
    // Normalize to standard format (e.g. "en-us" to "en-US")
    const normalizedLang = browserLang.replace(/-([a-z])/g, (_, letter) => `-${letter.toUpperCase()}`);
    
    // Look for exact match
    const exactMatch = availableLanguages.find(lang => lang.code === normalizedLang);
    if (exactMatch) {
      return exactMatch.code;
    }
    
    // Look for language match ignoring region (e.g. 'en' match with 'en-US')
    const langBase = browserLang.split('-')[0].toLowerCase();
    const baseMatch = availableLanguages.find(lang => 
      lang.code.toLowerCase().startsWith(langBase + '-')
    );
    
    if (baseMatch) {
      return baseMatch.code;
    }
  }
  
  // Default to English (US) if no match found
  const englishUs = availableLanguages.find(lang => lang.code === 'en-US');
  if (englishUs) {
    return englishUs.code;
  }
  
  // Fallback to English (UK) if English (US) not available
  const englishUk = availableLanguages.find(lang => lang.code === 'en-GB');
  if (englishUk) {
    return englishUk.code;
  }
  
  // Last resort: just use the first available language
  return availableLanguages[0]?.code || 'en-US';
}
